import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { StickyContainer } from '@sparefoot/react-core';
import { mapDispatchToActions } from '@sparefoot/react-core/utils';
import { Button } from 'components/core/Button';
import { SearchBox } from 'components/search/SearchBox';
import { Filter } from 'components/svg';
import { ClickToCall } from 'components/facility/ClickToCall';
import { phonePropType } from 'config/propTypes';
import { Paragraph, H1 } from 'components/core/Typography';
import MapPin from 'components/svg/MapPin';
import { getTollfreePhoneNumber } from 'store/modules/phone';
import { simpleAmenityTextMap } from '../../../config/amenityMap';

import './NewSearchToolbar.scss'; // ensure casing matches actual filename on disk
import { toTitleCase } from '../../../utils/formatting';

export function NewSearchToolbar({
	location,
	onSearch,
	onOpenFilters,
	filtersCount,
	className,
	phone,
	actions,
	sticky,
	hidden,
	amenityType,
	withinCallCenterHours,
	vehicle,
}) {
	const locationDetails =
		location && location.city && location.state
			? `${location.city}, ${location.state}`
			: '';

	return React.createElement(
		sticky ? StickyContainer : Fragment,
		sticky ? { collapse: hidden } : undefined,
		// Apply 'no-click-to-call' when outside call center hours to mirror footer flex sizing semantics.
		<div
			className={classnames(
				'new-search-toolbar',
				className,
				{ hidden },
				{ 'no-click-to-call': !withinCallCenterHours },
			)}
		>
			{!vehicle ? (
				<H1
					size="medium"
					color="white"
					weight="extraBold"
					className="new-search-toolbar__heading"
				>
					Cheap {simpleAmenityTextMap[amenityType] || ''} Self Storage{' '}
					{locationDetails && <span> in {locationDetails}</span>}
				</H1>
			) : (
				<H1
					size="medium"
					color="white"
					weight="extraBold"
					className="new-search-toolbar__heading"
				>
					Cheap Self Storage for{' '}
					{vehicle && `${toTitleCase(vehicle)}s`}
					{locationDetails && <span> in {locationDetails}</span>}
				</H1>
			)}

			{withinCallCenterHours && (
				<ClickToCall
					phone={phone}
					onFetchNumber={actions.getTollfreePhoneNumber}
					phoneIcon
					buttonText="Need Help ?"
				/>
			)}

			<Button
				type="button"
				className="new-search-toolbar__filter"
				onClick={onOpenFilters}
				aria-label="Open filters"
			>
				<Filter />
				<Paragraph
					color="white"
					size="small"
					weight="medium"
					last
				>
					Filter
				</Paragraph>
				{filtersCount > 0 && (
					<span
						className="new-search-toolbar__filter-count"
						aria-label={`${filtersCount} filters selected`}
					>
						{filtersCount}
					</span>
				)}
			</Button>
			<div className="tool-bar-search-box">
				<SearchBox
					id="new-search-toolbar-box"
					onSearch={onSearch}
					buttonText="Find Storage"
					icon={<MapPin fill="gray" />}
					inputLabel="Enter City or ZIP"
					locationParams={
						locationDetails
							? { location: locationDetails }
							: undefined
					}
					hideAutocompleteOnScroll
					useBrowserLocation
				/>
			</div>
		</div>,
	);
}

NewSearchToolbar.propTypes = {
	location: PropTypes.object,
	onSearch: PropTypes.func.isRequired,
	onOpenFilters: PropTypes.func.isRequired,
	filtersCount: PropTypes.number,
	className: PropTypes.string,
	phone: phonePropType,
	actions: PropTypes.shape({
		getTollfreePhoneNumber: PropTypes.func.isRequired,
	}).isRequired,
	sticky: PropTypes.bool,
	hidden: PropTypes.bool,
	withinCallCenterHours: PropTypes.bool.isRequired,
	amenityType: PropTypes.string,
	vehicle: PropTypes.string,
};

NewSearchToolbar.defaultProps = {
	filtersCount: 0,
	phone: null,
	sticky: false,
	hidden: false,
};

export const newSearchToolbarSelector = (state) => ({
	phone: state.phone.headerPhone,
});

export default compose(
	connect(
		newSearchToolbarSelector,
		{ getTollfreePhoneNumber },
		mapDispatchToActions,
	),
)(NewSearchToolbar);
